import { useEffect, useState } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { TranslationProvider } from './context/TranslationContext';
import { WaItemProvider } from './context/WaItemContext';
import { LoadingProvider } from './context/LoadingContext';
import { LoadingSystem } from './components';
import Layout from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute';
import ArticleToolsPage from './pages/ArticleToolsPage';
import ConfigPage from './pages/ConfigPage';
import LoginPage from './pages/LoginPage';
import AdminToolsPage from './pages/AdminToolsPage';
import RAGManagementPage from './pages/RAGManagementPage';
import CacheAdminPage from './pages/CacheAdminPage';
import ClientDataPage from './pages/ClientDataPage';
import CircuitBreakerTestPage from './pages/CircuitBreakerTestPage';
import RagArticlesPage from './pages/RagArticlesPage';
import ChatbotPage from './pages/ChatbotPage.jsx';
import FatalErrorPage from './components/FatalErrorPage';
import { applyTheme } from './styles/index';
import { post } from './utils/api-router';
import debug from '@commons-shared/debug';
import { config } from './config/environment';
import { initializeHandlebars } from './wa-tools/handlebarsHelpers';
import { getWorkerUrl } from '@commons-shared/workers-config.js';
import './App.css';

function App() {
  const [appState, setAppState] = useState({
    isLoading: true,
    error: null,
    isReady: false
  });

  useEffect(() => {
    // Charger et appliquer le thème sauvegardé (light par défaut)
    const savedTheme = localStorage.getItem('ui_theme') || 'light';
    applyTheme(savedTheme);
    debug.log(config, `Thème appliqué au démarrage: ${savedTheme}`);
    
    // Vérifier que le service d'encryption est disponible
    async function checkEncryptionService() {
      try {
        // Essayer d'abord le health check
        let healthCheckPassed = false;
        
        try {
          debug.log(config, 'Vérification de la disponibilité du worker via health check...');
          
          // Utiliser getWorkerUrl pour obtenir l'URL du worker config
          const configUrl = getWorkerUrl('CONFIG', 'auto');
          
          debug.log(config, 'URL du worker config utilisée:', configUrl);
          
          const healthUrl = `${configUrl}/api/config/health`;
          debug.log(config, `URL de health check: ${healthUrl}`);
          
          const healthResponse = await fetch(healthUrl);
          debug.log(config, `Réponse du health check: ${healthResponse.status}`);
          
          if (!healthResponse.ok) {
            debug.error(config, `Health check a échoué avec le statut: ${healthResponse.status}`);
            throw new Error(`Health check failed with status: ${healthResponse.status}`);
          }
          
          let healthData;
          try {
            const responseText = await healthResponse.text();
            debug.log(config, 'Texte brut de la réponse:', responseText);
            
            // Vérifie si la réponse ressemble à du HTML (contient des balises)
            if (responseText.includes('<html') || responseText.includes('<!DOCTYPE') || responseText.startsWith('<')) {
              debug.error(config, 'Réponse HTML reçue au lieu de JSON:', responseText.substring(0, 150) + '...');
              throw new Error('Le serveur a renvoyé une page HTML au lieu de JSON');
            }
            
            try {
              healthData = JSON.parse(responseText);
              debug.log(config, 'Données du health check (analysées):', healthData);
            } catch (jsonError) {
              debug.error(config, 'Erreur de parsing JSON:', jsonError);
              debug.error(config, 'Réponse non-JSON reçue:', responseText);
              
              // Si la réponse ressemble à du JSON mais est invalide
              if (responseText.includes('{') && responseText.includes('}')) {
                debug.warn(config, 'La réponse ressemble à du JSON mais est invalide, tentative de nettoyage...');
                // Tentative de nettoyage des caractères indésirables
                const cleanedText = responseText.replace(/[^\x20-\x7E]/g, '').trim();
                try {
                  healthData = JSON.parse(cleanedText);
                  debug.log(config, 'Données JSON après nettoyage:', healthData);
                } catch (e) {
                  throw new Error(`JSON invalide après nettoyage: ${e.message}`);
                }
              } else {
                throw new Error(`Réponse invalide du worker: ${jsonError.message}`);
              }
            }
          } catch (textError) {
            debug.error(config, 'Erreur lors de la lecture de la réponse:', textError);
            throw new Error(`Impossible de lire la réponse du worker: ${textError.message}`);
          }
          
          if (!healthData.success || healthData.status !== 'operational') {
            debug.error(config, 'Worker opérationnel mais signale un problème:', healthData);
            throw new Error('Worker reports non-operational status');
          }
          
          healthCheckPassed = true;
          debug.log(config, 'Health check réussi, worker opérationnel');
        } catch (healthError) {
          debug.error(config, 'Erreur lors du health check:', healthError);
          // Le worker n'est pas disponible du tout
          setAppState({
            isLoading: false,
            error: {
              type: 'worker_unavailable',
              message: `Le worker wab-config n'est pas disponible: ${healthError.message}`,
              solutions: [
                'Vérifiez que le worker wab-config est en cours d\'exécution sur l\'adresse et le port attendus.',
                'Vérifiez que l\'URL du worker est correctement configurée dans les variables d\'environnement.',
                'Essayez de redémarrer le worker wab-config.'
              ]
            },
            isReady: false
          });
          return;
        }
        
        // Tester le service d'encryption
        if (healthCheckPassed) {
          try {
            debug.log(config, 'Health check réussi, test du service d\'encryption...');
            
            // Utiliser l'API routée standard
            debug.log(config, 'Appel du service d\'encryption via l\'API router...');
            const encryptResponse = await post('/api/config/encrypt-data', { value: 'test' });
            debug.log(config, 'Réponse du service d\'encryption:', encryptResponse);
            
            if (!encryptResponse || !encryptResponse.success) {
              throw new Error('Le service a répondu mais n\'a pas pu chiffrer les données');
            }
            
            // Initialiser Handlebars après le succès des services
            debug.log(config, 'Initialisation de Handlebars...');
            try {
              await initializeHandlebars();
              debug.log(config, 'Handlebars initialisé avec succès');
            } catch (handlebarsError) {
              debug.error(config, 'Erreur lors de l\'initialisation de Handlebars:', handlebarsError);
              // Note: nous pouvons continuer même si Handlebars échoue, car ce n'est pas critique pour l'app
              debug.warn(config, 'L\'application continuera sans Handlebars - certaines fonctionnalités pourront être limitées');
            }
            
            setAppState({
              isLoading: false,
              error: null,
              isReady: true
            });
            debug.log(config, 'Application initialisée avec succès');
          } catch (encryptError) {
            debug.error(config, 'Erreur du service d\'encryption:', encryptError);
            
            // En cas d'erreur, essayer un appel direct pour le diagnostic
            try {
              debug.log(config, 'Tentative d\'appel direct au service d\'encryption...');
              const configUrl = getWorkerUrl('CONFIG', 'auto');
              const directResponse = await fetch(`${configUrl}/api/config/encrypt-data`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ value: 'test-direct' })
              });
              
              debug.log(config, 'Statut de la réponse directe:', directResponse.status);
              const directData = await directResponse.text();
              debug.log(config, 'Réponse directe brute:', directData);
              
              setAppState({
                isLoading: false,
                error: {
                  type: 'encryption_service_unavailable',
                  message: `Le service d'encryption a échoué via l'API router, mais fonctionne en direct. Problème potentiel de routage.`,
                  details: `Erreur initiale: ${encryptError.message}\nRéponse directe: ${directData}`,
                  solutions: [
                    'Vérifiez la configuration de l\'API router.',
                    'Vérifiez que les URLs sont correctement définies dans les variables d\'environnement.'
                  ]
                },
                isReady: false
              });
            } catch (directError) {
              debug.error(config, 'Erreur lors de l\'appel direct au service d\'encryption:', directError);
              setAppState({
                isLoading: false,
                error: {
                  type: 'encryption_service_unavailable',
                  message: `Le service d'encryption a échoué: ${encryptError.message}`,
                  details: `Erreur directe: ${directError.message}`,
                  solutions: [
                    'Vérifiez que la variable d\'environnement ENCRYPTION_KEY est correctement définie dans le worker.',
                    'Assurez-vous que la valeur de ENCRYPTION_KEY fait au moins 32 caractères.',
                    'Redémarrez le worker wab-config après avoir configuré la clé.'
                  ]
                },
                isReady: false
              });
            }
          }
        }
      } catch (error) {
        debug.error(config, 'Erreur critique lors de l\'initialisation de l\'application:', error);
        setAppState({
          isLoading: false,
          error: {
            type: 'unknown_error',
            message: `Erreur critique d'initialisation: ${error.message}`,
            details: error.stack,
            solutions: [
              'Vérifiez la console du navigateur pour les détails de l\'erreur.',
              'Vérifiez la configuration des variables d\'environnement.',
              'Assurez-vous que tous les workers sont en cours d\'exécution.'
            ]
          },
          isReady: false
        });
      }
    }
    
    checkEncryptionService();
  }, []);

  // Afficher un indicateur de chargement
  if (appState.isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="w-8 h-8 border-4 border-blue-500 rounded-full border-t-transparent animate-spin"></div>
      </div>
    );
  }

  // Afficher la page d'erreur fatale si le service d'encryption n'est pas disponible
  if (appState.error) {
    return <FatalErrorPage error={appState.error} />;
  }

  return (
    <LoadingProvider>
      <TranslationProvider>
        <AuthProvider>
          <WaItemProvider>
            <LoadingSystem />
            <Routes>
              {/* Route de login accessible sans authentification */}
              <Route path="/login" element={<LoginPage />} />
              
              {/* Route racine - redirection vers config */}
              <Route path="/" element={<Navigate to="/config" replace />} />
              
              {/* Routes protégées qui nécessitent une authentification */}
              <Route path="/tools" element={
                <ProtectedRoute>
                  <Layout>
                    <ArticleToolsPage />
                  </Layout>
                </ProtectedRoute>
              } />
              
              <Route path="/chatbot" element={
                <ProtectedRoute>
                  <Layout>
                    <ChatbotPage />
                  </Layout>
                </ProtectedRoute>
              } />
              
              <Route path="/config" element={
                <ProtectedRoute>
                  <Layout>
                    <ConfigPage />
                  </Layout>
                </ProtectedRoute>
              } />
                            
              {/* Route d'administration */}
              <Route path="/admin" element={
                <ProtectedRoute>
                  <Layout>
                    <AdminToolsPage />
                  </Layout>
                </ProtectedRoute>
              } />
              
              {/* Route de test des circuit breakers */}
              <Route path="/circuit-breaker-test" element={
                <ProtectedRoute>
                  <Layout>
                    <CircuitBreakerTestPage />
                  </Layout>
                </ProtectedRoute>
              } />
              
              {/* Route d'administration du cache */}
              <Route path="/cache-admin" element={
                <ProtectedRoute>
                  <Layout>
                    <CacheAdminPage />
                  </Layout>
                </ProtectedRoute>
              } />
              
              {/* Route de gestion RAG (indexation + articles) */}
              <Route path="/rag-management" element={
                <ProtectedRoute>
                  <Layout>
                    <RAGManagementPage />
                  </Layout>
                </ProtectedRoute>
              } />
              
              {/* Route des articles RAG (legacy - redirect to /rag-management) */}
              <Route path="/rag-articles" element={
                <ProtectedRoute>
                  <Layout>
                    <RagArticlesPage />
                  </Layout>
                </ProtectedRoute>
              } />
              
              {/* Route des données dynamiques client */}
              <Route path="/client-data" element={
                <ProtectedRoute>
                  <Layout>
                    <ClientDataPage />
                  </Layout>
                </ProtectedRoute>
              } />
              
              {/* Route par défaut - redirection vers config */}
              <Route path="*" element={<Navigate to="/config" replace />} />
            </Routes>
          </WaItemProvider>
        </AuthProvider>
      </TranslationProvider>
    </LoadingProvider>
  );
}

export default App;
